import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Trophy, Medal, Award, Target, Zap,
  Loader2, TrendingUp, Users
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stat type configurations
const STAT_CONFIGS = {
  touchdowns: { label: 'Touchdowns', icon: Trophy, color: 'text-yellow-500', unit: 'TD' },
  passing_yards: { label: 'Passing Yards', icon: Target, color: 'text-blue-500', unit: 'yds' },
  rushing_yards: { label: 'Rushing Yards', icon: Zap, color: 'text-orange-500', unit: 'yds' },
  receiving_yards: { label: 'Receiving Yards', icon: TrendingUp, color: 'text-purple-500', unit: 'yds' },
  interceptions: { label: 'Interceptions', icon: Award, color: 'text-red-500', unit: 'INT' }
};

// Rank Badge Component
const RankBadge = ({ rank }) => {
  if (rank === 1) return <div className="w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center"><Trophy className="w-5 h-5 text-white" /></div>;
  if (rank === 2) return <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center"><Medal className="w-5 h-5 text-white" /></div>;
  if (rank === 3) return <div className="w-8 h-8 rounded-full bg-amber-700 flex items-center justify-center"><Medal className="w-5 h-5 text-white" /></div>;
  return <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center font-bold text-sm">{rank}</div>;
};

// Leaderboard Row Component
const LeaderboardRow = ({ player, statType, rank }) => {
  const config = STAT_CONFIGS[statType];
  const statValue = player.stats?.[statType] || 0;
  
  return (
    <div 
      className={`flex items-center gap-4 p-4 rounded-lg border ${rank <= 3 ? 'bg-muted/50' : 'hover:bg-muted/30'} transition-colors`}
      data-testid={`leaderboard-row-${rank}`}
    >
      <RankBadge rank={rank} />
      
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-bold">
        {player.jersey_number || '?'}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold truncate">{player.name}</span>
          <Badge variant="outline" className="text-xs">{player.position}</Badge>
        </div>
        <p className="text-sm text-muted-foreground truncate">
          {player.team_name} • {player.age_category}
        </p>
      </div>
      
      <div className="text-right">
        <span className={`text-2xl font-bold ${config.color}`}>{statValue}</span>
        <span className="text-sm text-muted-foreground ml-1">{config.unit}</span>
      </div>
    </div>
  );
};

export const PlayerLeaderboard = () => {
  const [players, setPlayers] = useState([]);
  const [categories, setCategories] = useState({ stat_types: [], age_categories: [] });
  const [loading, setLoading] = useState(true);
  const [statType, setStatType] = useState('touchdowns');
  const [ageCategory, setAgeCategory] = useState('all');

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchLeaderboard();
  }, [statType, ageCategory]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/leaderboard/players/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ stat_type: statType, limit: '50' });
      if (ageCategory !== 'all') params.append('age_category', ageCategory);
      
      const res = await axios.get(`${API}/leaderboard/players?${params}`);
      setPlayers(res.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const config = STAT_CONFIGS[statType];
  const Icon = config?.icon || Trophy;

  return (
    <div className="container mx-auto px-4 py-8" data-testid="player-leaderboard-page">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold flex items-center justify-center gap-2">
          <Trophy className="h-8 w-8 text-yellow-500" />
          Player Leaderboard
        </h1>
        <p className="text-muted-foreground mt-2">
          Top performers across all teams
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-2 block">Stat Category</label>
              <Select value={statType} onValueChange={setStatType}>
                <SelectTrigger data-testid="stat-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STAT_CONFIGS).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>
                      {cfg.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-2 block">Age Category</label>
              <Select value={ageCategory} onValueChange={setAgeCategory}>
                <SelectTrigger data-testid="age-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ages</SelectItem>
                  {categories.age_categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stat Type Tabs */}
      <Tabs value={statType} onValueChange={setStatType} className="mb-6">
        <TabsList className="grid w-full grid-cols-5">
          {Object.entries(STAT_CONFIGS).map(([key, cfg]) => {
            const TabIcon = cfg.icon;
            return (
              <TabsTrigger key={key} value={key} className="flex items-center gap-1">
                <TabIcon className={`h-4 w-4 ${cfg.color}`} />
                <span className="hidden sm:inline">{cfg.label.split(' ')[0]}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${config.color}`} />
            Top {config.label}
          </CardTitle>
          <CardDescription>
            {ageCategory === 'all' ? 'All age categories' : ageCategory}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : players.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No player stats recorded yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Stats are automatically tracked when game scores are reported
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {players.map((player, index) => (
                <LeaderboardRow 
                  key={player.id} 
                  player={player} 
                  statType={statType}
                  rank={player.rank || index + 1}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
